import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Clock3, Mail, ShieldAlert, Trash2, Database, Lock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const steps = [
  {
    icon: Mail,
    title: '1. Envoyer la demande',
    text: 'Envoyez un email à admin@ecarbu.com avec le sujet “Suppression de compte E-Carbu Driver”. Indiquez le nom du compte, l’adresse email utilisée et, si possible, l’organisation concernée.',
  },
  {
    icon: BadgeCheck,
    title: '2. Vérification d’identité',
    text: 'L’équipe de gestion vérifie que la demande provient du titulaire du compte ou d’un administrateur autorisé avant toute action.',
  },
  {
    icon: Trash2,
    title: '3. Suppression ou anonymisation',
    text: 'Une fois la demande validée, le compte est désactivé puis les données associées sont supprimées ou anonymisées selon leur nature et les obligations légales.',
  },
];

const deletedData = [
  'Identifiants de connexion, jetons de session et données de profil associées au compte.',
  'Données mémorisées par l’application elle-même, effacées dès que cela est techniquement possible.',
  'Associations directes entre le compte et les éléments d’usage non requis pour la conformité.',
];

const retainedData = [
  'Les documents, journaux d’audit et éléments opérationnels que la loi ou les besoins de sécurité imposent de conserver.',
  'Les enregistrements déjà inclus dans les sauvegardes techniques pendant la fenêtre de rétention de secours.',
  'Les données qui doivent être conservées de façon minimale et confidentielle pour respecter les obligations légales ou comptables.',
];

export default function AccountDeletionPage() {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,109,0,0.18),_transparent_30%),linear-gradient(180deg,_#fff7f0_0%,_#ffffff_42%,_#f8fafc_100%)] text-foreground">
      <main className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <section className="relative overflow-hidden rounded-[2rem] border border-flame/20 bg-card shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-flame via-flame to-readout" />
          <div className="absolute -right-20 top-12 h-52 w-52 rounded-full bg-flame/10 blur-3xl" />
          <div className="absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-muted blur-3xl" />

          <div className="relative grid gap-8 px-6 py-10 sm:px-10 sm:py-12 lg:grid-cols-[1.35fr_0.95fr] lg:items-end">
            <div className="max-w-3xl space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-flame/30 bg-flame/10 px-4 py-2 text-sm font-semibold text-flame-ink shadow-sm">
                <ShieldAlert className="h-4 w-4" />
                URL de suppression de compte
              </div>
              <div className="space-y-4">
                <h1 className="max-w-2xl text-4xl font-black tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                  Demander la suppression de votre compte E-Carbu Driver
                </h1>
                <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                  Cette page explique comment demander la suppression de votre compte et des données associées dans
                  E-Carbu Driver. La procédure est séparée du reste de la politique de confidentialité pour que le lien
                  soit facile à trouver depuis la fiche Google Play Store.
                </p>
              </div>
            </div>

            <div className="relative rounded-[1.75rem] border border-border bg-readout p-5 text-slate-100 shadow-2xl shadow-slate-900/10">
              <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-flame">E-Carbu Driver</p>
                  <p className="mt-2 text-lg font-semibold text-white">Procédure publique</p>
                </div>
                <div className="rounded-2xl bg-white/10 px-3 py-2 text-xs font-semibold text-orange-200">
                  Public
                </div>
              </div>

              <div className="grid gap-4 pt-5 text-sm text-muted-foreground/70">
                <div className="rounded-2xl bg-white/5 px-4 py-4">
                  <p className="font-semibold text-white">Canal de demande</p>
                  <p className="mt-1 leading-6">Email dédié à l’équipe de gestion du compte.</p>
                </div>
                <div className="rounded-2xl bg-white/5 px-4 py-4">
                  <p className="font-semibold text-white">Délai de traitement</p>
                  <p className="mt-1 leading-6">La demande est traitée après vérification de l’identité et du droit d’accès.</p>
                </div>
                <div className="rounded-2xl bg-white/5 px-4 py-4">
                  <p className="font-semibold text-white">Conservation résiduelle</p>
                  <p className="mt-1 leading-6">Certaines données peuvent rester dans les sauvegardes ou les journaux obligatoires.</p>
                </div>
              </div>

              <a
                href="mailto:admin@ecarbu.com?subject=Suppression%20de%20compte%20E-Carbu%20Driver"
                className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Demander la suppression par email
              </a>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-3">
          {steps.map((step) => {
            const Icon = step.icon;

            return (
              <Card key={step.title} className="group border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                    <Icon className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-xl text-foreground">{step.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
                  <p>{step.text}</p>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-2">
          <Card className="border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                <Database className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl text-foreground">Données supprimées</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
              {deletedData.map((item) => (
                <div key={item} className="flex gap-3 rounded-2xl bg-muted/50 px-4 py-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <p>{item}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                <Lock className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl text-foreground">Données conservées</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
              {retainedData.map((item) => (
                <div key={item} className="flex gap-3 rounded-2xl bg-muted/50 px-4 py-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <p>{item}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg lg:col-span-2">
            <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                <Clock3 className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl text-foreground">Durée de conservation supplémentaire</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl bg-muted/50 px-4 py-4">
                  <p className="font-semibold text-foreground">Sauvegardes techniques</p>
                  <p className="mt-1">
                    Les données peuvent rester dans les sauvegardes de secours pendant une période limitée pouvant aller
                    jusqu’à 30 jours avant suppression automatique.
                  </p>
                </div>
                <div className="rounded-2xl bg-muted/50 px-4 py-4">
                  <p className="font-semibold text-foreground">Obligations légales et audit</p>
                  <p className="mt-1">
                    Certains journaux ou documents peuvent être conservés plus longtemps si la loi, la sécurité ou la
                    conformité l’exigent.
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-flame/20 bg-flame/10 px-4 py-4 text-muted-foreground">
                <p className="font-semibold text-foreground">Point important</p>
                <p className="mt-1 leading-6">
                  Les données GPS en direct ne sont pas conservées comme historique utilisateur dans l’application chauffeur.
                  Elles servent au suivi temps réel via websocket et ne font donc pas partie d’un historique à supprimer.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <div className="mt-8 rounded-[1.75rem] border border-border bg-readout px-6 py-6 text-slate-100 shadow-lg shadow-slate-900/20 sm:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-flame">Navigation</p>
          <p className="mt-2 text-base leading-7 text-slate-200">
            Cette page est publique et peut être utilisée comme URL de suppression de compte dans la fiche Google Play Store.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <a
              href="mailto:admin@ecarbu.com?subject=Suppression%20de%20compte%20E-Carbu%20Driver"
              className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Demander la suppression par email
            </a>
            <Link
              to="/privacy-policy"
              className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Lire la politique de confidentialité
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}